import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import { ArrowBack, CalendarToday, FitnessCenter, Business } from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PaymentProcessingModal from 'components/shared/modals/PaymentProcessingModal';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import AppBarContext from 'context/AppBarContext';
import uiSlice from 'store/modules/ui';
import authSlice from 'store/modules/auth';
import { createSnack } from 'common/utils/general';
import { isAdmin } from 'common/utils/user';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import routes from 'common/routes';
import usePaymentProcessing from 'hooks/payments/usePaymentProcessing';
import useIpGeolocation from 'hooks/payments/useIpGeolocation';
import paymentApi from 'api/rest/payment';
import logoSrc from 'images/logo.svg';

const PLAN_QUERY = gql`
  query PublicPlanDetail($id: ID!) {
    plan(id: $id) {
      id
      name
      description
      image_url
      type
      price_usd
      price_cop
      sessions
      is_subscribable
      subscription_interval
      org_benefit {
        discount_percent
        base_price
        discounted_price
        currency
        organization_name
      }
      session_types {
        id
        name
      }
    }
  }
`;

const MY_PLAN_REQUESTS_QUERY = gql`
  query MyPlanRequests {
    myPlanRequests {
      id
      status
      rejection_reason
      plan {
        id
      }
    }
  }
`;

const REQUEST_PLAN_ACQUISITION_MUTATION = gql`
  mutation RequestPlanAcquisition($plan_id: ID!) {
    requestPlanAcquisition(plan_id: $plan_id) {
      id
      status
    }
  }
`;

// Acento dorado de selección en el toggle de facturación (mismo patrón que el editor).
const billingToggleSx = {
  '& .MuiToggleButton-root': {
    color: 'text.secondary',
    borderColor: 'hairline.strong',
    fontWeight: 700,
    textTransform: 'none',
    py: 1.1
  },
  '& .Mui-selected': {
    color: 'gold.main !important',
    backgroundColor: 'surface.overlay !important'
  },
  '& .Mui-selected:hover': { backgroundColor: 'rgba(255,255,255,0.08) !important' }
};

const ctaButtonSx = { ...premiumButtonSx, py: 1.3 };

function PublicPlanDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'buttons']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const [requested, setRequested] = useState(false);

  const { id: userId, country: userCountry, role } = useSelector((state) => state.auth.user);
  const isAdminUser = isAdmin(role);
  const { currency: detectedCurrency, gateway: detectedGateway, gatewayEnabled } = useIpGeolocation();

  const userCurrency = (() => {
    if (detectedCurrency) return detectedCurrency;
    try {
      const label = JSON.parse(userCountry)?.label;
      return label === 'Colombia' ? 'COP' : 'USD';
    } catch {
      return 'USD';
    }
  })();

  const { modalOpen, status: paymentStatus, timeRemaining, startProcessing, retryPayment, closeModal } =
    usePaymentProcessing(userId);

  const [billingMode, setBillingMode] = useState('subscription');

  const { data, loading } = useQuery(PLAN_QUERY, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const { data: requestsData } = useQuery(MY_PLAN_REQUESTS_QUERY, {
    fetchPolicy: 'network-only'
  });

  const existingRequest = requestsData?.myPlanRequests?.find(
    (r) => String(r.plan.id) === String(id)
  ) ?? null;

  const [requestAcquisition, { loading: requesting }] = useMutation(
    REQUEST_PLAN_ACQUISITION_MUTATION
  );

  const plan = data?.plan;
  const hasPlanPrice = !!(plan?.price_cop || plan?.price_usd);
  const canPayWithWompi = hasPlanPrice && userCurrency === 'COP';
  // Suscripción disponible solo para planes habilitados, en COP (Wompi) y con precio.
  const canSubscribe = !!(plan?.is_subscribable && plan?.price_cop && userCurrency === 'COP');
  // Suscripción internacional (USD) vía Stripe — solo si Stripe está habilitado.
  const canStripeSubscribe = !!(
    plan?.is_subscribable && userCurrency === 'USD' && detectedGateway === 'stripe' && gatewayEnabled
  );

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(plan?.name || t('plan.detailTitle', { ns: 'common', defaultValue: 'Detalle del plan' }));
  }, [setAppBarButton, setAppBarTitle, plan, t]);

  const handleAcquire = async () => {
    if (canSubscribe && billingMode === 'subscription') {
      navigate(`/checkout/subscribe/${id}`);
    } else if (canPayWithWompi) {
      startProcessing(id);
    } else {
      try {
        await requestAcquisition({ variables: { plan_id: id } });
        setRequested(true);
        dispatch(authSlice.actions.me('platform'));
      } catch (err) {
        dispatch(
          uiSlice.actions.enqueueSnackbar(
            createSnack({
              key: 'plan-request-error',
              variant: 'error',
              message: t('plan.requestError', {
                ns: 'common',
                defaultValue: 'Hubo un error al enviar tu solicitud. Intenta de nuevo.'
              })
            })
          )
        );
      }
    }
  };

  const handleStripeSubscribe = async () => {
    try {
      const { url } = await paymentApi.createStripeSubscriptionCheckout({ plan_id: id });
      window.location.href = url; // Checkout hosteado de Stripe (redirección estándar)
    } catch (err) {
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({
            key: 'stripe-subscribe-error',
            variant: 'error',
            message: err?.message || t('plan.requestError', {
              ns: 'common',
              defaultValue: 'Hubo un error. Intenta de nuevo.'
            })
          })
        )
      );
    }
  };

  const handleModalClose = () => {
    const wasSuccess = paymentStatus === 'success';
    closeModal();
    if (wasSuccess) navigate(routes.listClientPayments.client.path);
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress sx={{ color: 'gold.main' }} />
      </Stack>
    );
  }

  if (!plan) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 480, mx: 'auto' }}>
        <Alert severity="error" variant="filled">
          {t('plan.notFound', { ns: 'common', defaultValue: 'Plan no encontrado' })}
        </Alert>
      </Stack>
    );
  }

  // Precios por geolocalización: el cliente ve SOLO su moneda; el admin (quien los
  // administra) ve ambas.
  const showCop = !!plan.price_cop && (isAdminUser || userCurrency === 'COP');
  const showUsd = !!plan.price_usd && (isAdminUser || userCurrency === 'USD');
  const hasShownPrice = showCop || showUsd;

  // Tarifa corporativa (auto por membresía). Solo cliente miembro de una org; admin no la tiene.
  const orgBenefit = plan.org_benefit && plan.org_benefit.discount_percent > 0 ? plan.org_benefit : null;
  const orgCop = orgBenefit && orgBenefit.currency === 'COP';
  const orgUsd = orgBenefit && orgBenefit.currency === 'USD';

  const priceBlock = (
    <Stack spacing={0.25}>
      {showCop && (orgCop ? (
        <>
          <Typography sx={{ color: 'text.secondary', textDecoration: 'line-through', fontWeight: 700, fontSize: '1.05rem' }}>
            ${Number(plan.price_cop).toLocaleString('es-CO')} COP
          </Typography>
          <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '2rem', lineHeight: 1.1 }}>
            ${Number(orgBenefit.discounted_price).toLocaleString('es-CO')}
            <Box component="span" sx={{ fontSize: '0.45em', color: 'text.secondary', ml: 0.75, fontWeight: 700 }}>
              COP
            </Box>
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '2rem', lineHeight: 1.1 }}>
          ${Number(plan.price_cop).toLocaleString('es-CO')}
          <Box component="span" sx={{ fontSize: '0.45em', color: 'text.secondary', ml: 0.75, fontWeight: 700 }}>
            COP
          </Box>
        </Typography>
      ))}
      {showUsd && (orgUsd ? (
        <>
          <Typography sx={{ color: 'text.secondary', textDecoration: 'line-through', fontWeight: 700, fontSize: '0.95rem' }}>
            ${Number(plan.price_usd).toFixed(2)} USD
          </Typography>
          <Typography sx={{ fontWeight: showCop ? 700 : 800, color: showCop ? 'text.secondary' : 'text.primary', fontSize: showCop ? '1rem' : '2rem', lineHeight: 1.1 }}>
            ${Number(orgBenefit.discounted_price).toFixed(2)}
            <Box component="span" sx={{ fontSize: showCop ? '0.85em' : '0.45em', ml: 0.75, fontWeight: 700, color: 'text.secondary' }}>
              USD
            </Box>
          </Typography>
        </>
      ) : (
        <Typography
          sx={{
            fontWeight: showCop ? 700 : 800,
            color: showCop ? 'text.secondary' : 'text.primary',
            fontSize: showCop ? '1rem' : '2rem',
            lineHeight: 1.1
          }}>
          ${Number(plan.price_usd).toFixed(2)}
          <Box component="span" sx={{ fontSize: showCop ? '0.85em' : '0.45em', ml: 0.75, fontWeight: 700, color: 'text.secondary' }}>
            USD
          </Box>
        </Typography>
      ))}
      {!hasShownPrice && (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('plan.detail.consultPrice', { ns: 'common' })}
          </Typography>
        </Stack>
      )}
    </Stack>
  );

  const purchasePanel = (
    <Box
      sx={{
        borderRadius: 3,
        backgroundColor: 'surface.content',
        border: '1px solid',
        borderColor: 'hairline.main',
        p: { xs: 2.5, md: 3 },
        height: '100%'
      }}>
      <Stack spacing={2.5}>
        {orgBenefit && (
          <Chip
            icon={<Business sx={{ fontSize: 16, color: '#0A0A0A !important' }} />}
            label={`${orgBenefit.organization_name} · -${orgBenefit.discount_percent}%`}
            sx={{ alignSelf: 'flex-start', maxWidth: '100%', backgroundColor: 'gold.main', color: '#0A0A0A', fontWeight: 800 }}
          />
        )}
        {priceBlock}

        {hasShownPrice && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -2 }}>
            {canSubscribe && billingMode === 'subscription'
              ? t('plan.perMonthCadence', { ns: 'common', defaultValue: 'al mes · se renueva automáticamente' })
              : t('plan.oneTimeCadence', { ns: 'common', defaultValue: 'pago único' })}
          </Typography>
        )}

        {canPayWithWompi && (
          <Stack spacing={1.5}>
            {canSubscribe && (
              <ToggleButtonGroup
                value={billingMode}
                exclusive
                fullWidth
                onChange={(e, val) => val && setBillingMode(val)}
                sx={billingToggleSx}>
                <ToggleButton value="subscription">
                  {t('plan.subscribeMonthly', { ns: 'common', defaultValue: 'Suscripción mensual' })}
                </ToggleButton>
                <ToggleButton value="one_time">
                  {t('plan.oneTime', { ns: 'common', defaultValue: 'Pago único' })}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <Button fullWidth onClick={handleAcquire} sx={ctaButtonSx}>
              {canSubscribe && billingMode === 'subscription'
                ? t('plan.subscribe', { ns: 'common', defaultValue: 'Suscribirme' })
                : t('plan.acquire', { ns: 'common', defaultValue: 'Adquirir plan' })}
            </Button>
            {canSubscribe && billingMode === 'subscription' && (
              <Typography variant="caption" textAlign="center" sx={{ color: 'text.secondary' }}>
                {t('plan.cancelAnytime', { ns: 'common', defaultValue: 'Cancela cuando quieras' })}
              </Typography>
            )}
          </Stack>
        )}

        {!canPayWithWompi && canStripeSubscribe && (
          <Stack spacing={1.5}>
            <Button fullWidth onClick={handleStripeSubscribe} sx={ctaButtonSx}>
              {t('plan.subscribe', { ns: 'common', defaultValue: 'Suscribirme' })}
            </Button>
            <Typography variant="caption" textAlign="center" sx={{ color: 'text.secondary' }}>
              {t('plan.cancelAnytime', { ns: 'common', defaultValue: 'Cancela cuando quieras' })}
            </Typography>
          </Stack>
        )}

        {!canPayWithWompi && !canStripeSubscribe && (
          <Stack spacing={1.5}>
            {(requested || existingRequest?.status === 'PENDING') && (
              <Alert severity="info" variant="filled" sx={{ borderRadius: 2 }}>
                {t('plan.requestSent', {
                  ns: 'common',
                  defaultValue: 'Tu solicitud ha sido enviada. Un asesor de OTRIX te contactará pronto.'
                })}
              </Alert>
            )}
            {existingRequest?.status === 'APPROVED' && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {t('plan.detail.requestApproved', { ns: 'common' })}
              </Alert>
            )}
            {existingRequest?.status === 'REJECTED' && (
              <Box>
                <Alert
                  severity="error"
                  sx={{ borderRadius: 2, mb: existingRequest.rejection_reason ? 1.5 : 0 }}>
                  {t('plan.detail.requestRejected', { ns: 'common' })}
                </Alert>
                {existingRequest.rejection_reason && (
                  <Box
                    sx={{
                      backgroundColor: 'surface.overlay',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'hairline.main',
                      px: 2,
                      py: 1.5
                    }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      {t('plan.detail.reason', { ns: 'common' })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {existingRequest.rejection_reason}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {!existingRequest && !requested && (
              <Button fullWidth disabled={requesting} onClick={handleAcquire} sx={ctaButtonSx}>
                {t('plan.acquire', { ns: 'common', defaultValue: 'Adquirir plan' })}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );

  return (
    <>
      <SEOPageTitle title={plan.name} />

      {/* Botón volver */}
      <Box
        onClick={() => navigate(routes.clientDashboard.path)}
        sx={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'text.secondary',
          mb: 3,
          '&:hover': { color: 'gold.main' }
        }}>
        <ArrowBack fontSize="small" />
        <Typography variant="body2">{t('back', { ns: 'common' })}</Typography>
      </Box>

      <PremiumCard padding={{ xs: 2, md: 3.5 }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          {/* Columna izquierda: visual + info del plan */}
          <Grid xs={12} md={7}>
            <Stack spacing={2.5}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 200, md: 280 },
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'hairline.main',
                  background: 'linear-gradient(135deg, #1C1C1C 0%, #121212 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                {plan.image_url ? (
                  <Box
                    component="img"
                    src={plan.image_url}
                    alt={plan.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={logoSrc}
                    alt="OTRIX"
                    sx={{ height: 96, opacity: 0.85, filter: 'var(--otx-logo-filter)' }}
                  />
                )}
              </Box>

              <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: { xs: '1.6rem', md: '2rem' }, letterSpacing: '-0.02em' }}>
                {plan.name}
              </Typography>

              {plan.sessions > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <FitnessCenter sx={{ fontSize: 18, color: 'gold.main' }} />
                  <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    {plan.sessions}{' '}
                    {t('plan.sessions', { ns: 'common', defaultValue: 'sesiones incluidas' })}
                  </Typography>
                </Stack>
              )}

              {plan.session_types?.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {plan.session_types.map((st) => (
                    <Chip
                      key={st.id}
                      label={st.name}
                      size="small"
                      sx={{ backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 700 }}
                    />
                  ))}
                </Stack>
              )}

              {plan.description && (
                <>
                  <Divider sx={{ borderColor: 'hairline.main' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {plan.description}
                  </Typography>
                </>
              )}
            </Stack>
          </Grid>

          {/* Columna derecha: precio + compra */}
          <Grid xs={12} md={5}>
            {purchasePanel}
          </Grid>
        </Grid>
      </PremiumCard>

      <PaymentProcessingModal
        open={modalOpen}
        status={paymentStatus}
        timeRemaining={timeRemaining}
        onClose={handleModalClose}
        onRetryPayment={() => retryPayment()}
      />
    </>
  );
}

export default PublicPlanDetail;
